import { useEffect, useId, useRef, useState } from 'react'

type CaptchaStatus = 'loading' | 'waiting' | 'verified' | 'error'

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string
          callback: (token: string) => void
          'expired-callback': () => void
          'error-callback': () => void
          'timeout-callback': () => void
          theme: 'auto'
        }
      ) => string
      remove: (widgetId: string) => void
    }
  }
}

export function Captcha({ onToken }: { onToken: (token: string) => void }) {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined
  const ref = useRef<HTMLDivElement>(null)
  const id = useId()
  const [status, setStatus] = useState<CaptchaStatus>('loading')
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!siteKey) {
      onToken('development')
      setStatus('verified')
      return
    }

    onToken('')
    setStatus('loading')
    let active = true
    let widgetId: string | undefined
    let script: HTMLScriptElement | undefined

    const fail = () => {
      if (!active) return
      onToken('')
      setStatus('error')
    }

    const render = () => {
      if (!active || !ref.current || !window.turnstile || widgetId) return

      try {
        setStatus('waiting')
        widgetId = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (token) => {
            if (!active) return
            onToken(token)
            setStatus('verified')
          },
          'expired-callback': () => {
            if (!active) return
            onToken('')
            setStatus('waiting')
          },
          'error-callback': fail,
          'timeout-callback': fail,
          theme: 'auto',
        })
      } catch {
        fail()
      }
    }

    const handleLoad = () => {
      if (script) script.dataset.dotitTurnstileLoaded = 'true'
      render()
    }
    const handleError = () => {
      if (script) script.dataset.dotitTurnstileFailed = 'true'
      fail()
    }

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-dotit-turnstile="true"]'
    )

    if (window.turnstile) {
      render()
    } else {
      if (existing?.dataset.dotitTurnstileFailed === 'true') {
        existing.remove()
      }

      script = existing?.isConnected
        ? existing
        : document.createElement('script')
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.dataset.dotitTurnstile = 'true'
      script.addEventListener('load', handleLoad)
      script.addEventListener('error', handleError)

      if (!script.isConnected) {
        document.head.append(script)
      } else if (script.dataset.dotitTurnstileLoaded === 'true') {
        render()
      }
    }

    const loadTimeout = window.setTimeout(() => {
      if (!widgetId) fail()
    }, 12_000)

    return () => {
      active = false
      window.clearTimeout(loadTimeout)
      script?.removeEventListener('load', handleLoad)
      script?.removeEventListener('error', handleError)
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId)
      }
    }
  }, [attempt, onToken, siteKey])

  return (
    <div className='space-y-2'>
      <div id={id} ref={ref} aria-label='Human verification' />
      {status === 'loading' && (
        <p className='text-xs text-muted-foreground'>
          Loading human verification…
        </p>
      )}
      {status === 'waiting' && (
        <p className='text-xs text-muted-foreground'>
          Complete the human verification to continue.
        </p>
      )}
      {status === 'error' && (
        <div
          className='flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700'
          role='alert'
        >
          <span>Human verification could not load.</span>
          <button
            className='font-semibold underline underline-offset-2'
            type='button'
            onClick={() => setAttempt((value) => value + 1)}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  )
}
